import {
  round,
  roundResolutionValues,
  roundToNearestEven,
} from 'src/domains/calculators/utilities/round';
import { getAspectRatio } from 'src/domains/calculators/utilities/getAspectRatio';
import { getMegaPixelCount } from 'src/domains/calculators/utilities/getMegaPixelCount';
import {
  AspectRatio,
  Resolution,
} from 'src/domains/calculators/utilities/types';
import { ceilingResolutionValues } from 'src/domains/calculators/utilities/ceiling';
import { FramingAndWorkingResolutionOutputProps } from './types';

interface FramingAndWorkingResolutionInputProps {
  aspectRatio: number;
  squeeze: number;
  canvasResolution: Resolution;
  protection: number;
  framingDecisionResolution: Resolution;
}

interface WithRawResult<T> {
  raw: T;
  rounded: T;
}

/**
 * Calculates the IMF (Interoperable Mastering Format) Active Image Resolution
 *
 * This determines the standard IMF extraction resolution based on the output aspect ratio.
 * IMF is a standardized format for digital cinema mastering that defines specific
 * resolutions based on whether the content is wider or narrower than 16:9 (1.778).
 *
 * Resolution logic:
 * - For aspect ratios > 1.778 (wider than 16:9): Use 3840 width, calculate height
 * - For aspect ratios ≤ 1.778 (narrower than 16:9): Use 2160 height, calculate width
 *
 * @example
 * // For 2.39:1 aspect ratio (wider than 16:9):
 * // horizontal = 3840, vertical = 3840 / 2.39 = 1607.1
 * // Returns: { raw: [3840, 1607.1], rounded: [3840, 1608] }
 *
 * // For 1.33:1 aspect ratio (narrower than 16:9):
 * // horizontal = 2160 * 1.33 = 2872.8, vertical = 2160
 * // Returns: { raw: [2872.8, 2160], rounded: [2874, 2160] }
 */
const getIMFActiveImageResolution = (
  outputAspectRatio: number
): WithRawResult<Resolution> => {
  const width =
    outputAspectRatio > 1.7777777777777 ? 3840 : 2160 * outputAspectRatio;

  const height =
    outputAspectRatio < 1.7777777777777 ? 2160 : 3840 / outputAspectRatio;

  const raw = { width, height } as Resolution;
  const rounded = {
    width: roundToNearestEven(width),
    height: roundToNearestEven(height),
  };

  return {
    raw,
    rounded,
  };
};

/**
 * Calculates the Maximum Allowed Sensor Crop resolution
 *
 * This determines the maximum resolution that can be extracted from the sensor
 * while maintaining Netflix UHD compliance (minimum 8.3 megapixels). For spherical
 * lenses, this uses the IMF standard. For anamorphic lenses, it calculates the
 * optimal resolution based on the 8.3MP threshold and lens squeeze factor.
 *
 * Netflix UHD Requirements:
 * - Minimum 8.3 megapixels (8,297,000 pixels)
 * - Content must maintain proper aspect ratios after desqueeze
 *
 * @example
 * // For anamorphic 1.5x lens with 2.39:1 output:
 * // Calculates optimal resolution maintaining 8.3MP minimum
 * // Returns: { raw: [4052.8, 2547.4], rounded: [4054, 2548] }
 */
const getMaximumAllowedSensorCrop = ({
  squeeze,
  aspectRatio,
}: FramingAndWorkingResolutionInputProps): WithRawResult<Resolution> => {
  if (squeeze === 1) {
    return getIMFActiveImageResolution(aspectRatio);
  }

  const amountOfPixels = 8297000; // Netflix UHD minimum pixel count

  const divided = aspectRatio / squeeze;
  const multiplied = aspectRatio * squeeze;

  const width = Math.sqrt(amountOfPixels / divided) * divided;

  const height = Math.sqrt(amountOfPixels / multiplied) * squeeze;

  const raw = { width, height } as Resolution;
  const rounded = ceilingResolutionValues(roundResolutionValues(raw), 2);

  return {
    raw,
    rounded,
  };
};

/**
 * Calculates the Maximum Allowed Resize percentage in post-production
 *
 * This determines how much the active image resolution exceeds the Netflix UHD
 * compliance threshold. Values over 100% indicate that post-production resizing
 * would be required to meet compliance, with higher percentages representing
 * more aggressive scaling that may compromise quality.
 *
 * The calculation compares the active image width to the maximum allowed sensor
 * crop width, expressing the ratio as a percentage.
 *
 * @example
 * // If active image is 4000x2000 and max allowed is 3840x1920:
 * // Calculation: (4000 / 3840) * 100 = 104.17%
 * // Returns: { raw: 104.17, rounded: 104 }
 */
const getMaximumAllowedResizeInPost = (
  framingDecisionResolution: Resolution,
  maximumAllowedSensorCrop: Resolution
): WithRawResult<number> => {
  const raw = framingDecisionResolution.width / maximumAllowedSensorCrop.width;
  const asPercentage = raw * 100;
  const rounded = round(asPercentage);

  return {
    raw: asPercentage,
    rounded,
  };
};

/**
 * Calculates the Preferred Working Resolution for post-production
 *
 * This determines the optimal working resolution that maintains the relationship
 * between the IMF standard resolution and the active image area, scaled up to the
 * full sensor dimensions. This provides the recommended resolution for editing
 * and post-production workflows while preserving maximum image quality.
 *
 * The calculation:
 * 1. Find the scaling factor between IMF and active image resolutions
 * 2. Apply this factor to the full sensor width
 * 3. Calculate height based on the content aspect ratio (post-desqueeze)
 *
 * @example
 * // IMF: [3840, 1608], Active: [3892, 1630], Sensor: [4096, 2160], Content: [2.39, 1]
 * // Scale factor: 3840/3892 = 0.9866, Horizontal: 0.9866 * 4096 = 4041
 * // Vertical: 4041 / 2.39 = 1691, Returns: { raw: [4041, 1691], rounded: [4042, 1692] }
 */
const getPreferredWorkingResolution = (
  imfActiveImageResolution: Resolution,
  framingDecisionResolution: Resolution,
  sensorResolution: Resolution,
  contentAspectRatio: AspectRatio
): WithRawResult<Resolution> => {
  const width =
    (imfActiveImageResolution.width / framingDecisionResolution.width) *
    sensorResolution.width;
  const height = width / contentAspectRatio[0];

  const raw = { width, height } as Resolution;
  const rounded = ceilingResolutionValues(raw, 2);

  return {
    raw,
    rounded,
  };
};

/**
 * Calculate usable area after safety crop
 * Example: 5% safety crop = 0.05 -> 0.95 usable area
 */
function normalizeSafetyCrop(safetyCrop: number): number {
  return 1 - safetyCrop;
}

/**
 * Calculates the Minimum Working Resolution for post-production
 *
 * This determines the minimum resolution needed in post-production workflows
 * to account for safety crops while maintaining IMF standard compliance. The
 * calculation scales up the IMF resolution by the inverse of the safety crop
 * percentage to ensure sufficient headroom for framing adjustments.
 *
 * @example
 * // IMF: [3840, 1608] with 5% safety crop:
 * // Normalized crop: (100-5)/100 = 0.95
 * // Horizontal: 3840 / 0.95 = 4042, Vertical: 1608 / 0.95 = 1693
 * // Returns: { raw: [4042, 1693], rounded: [4042, 1694] }
 */
const getMinimumWorkingResolution = (
  imfActiveImageResolution: Resolution,
  safetyCrop: number
): WithRawResult<Resolution> => {
  const normalizedSafetyCrop = normalizeSafetyCrop(safetyCrop);
  const width = imfActiveImageResolution.width / normalizedSafetyCrop;
  const height = imfActiveImageResolution.height / normalizedSafetyCrop;

  const raw = { width, height } as Resolution;
  const rounded = {
    width: roundToNearestEven(width),
    height: roundToNearestEven(height),
  };

  return {
    raw,
    rounded,
  };
};

/**
 * Calculates the Dailies Active Image Resolution
 *
 * This determines the resolution for dailies (rough cut/preview) versions of the
 * content by halving the IMF active image resolution. Dailies are typically lower
 * resolution versions used during editing and review processes to reduce file sizes
 * and improve workflow performance while maintaining aspect ratio accuracy.
 *
 * @example
 * // For IMF resolution of [3840, 1608]:
 * // Horizontal: 3840 / 2 = 1920, Vertical: 1608 / 2 = 804
 * // Returns: { raw: [1920, 804], rounded: [1920, 804] }
 */
const getDailiesActiveImageResolution = (
  imfActiveImageResolution: Resolution
) => {
  const width = imfActiveImageResolution.width / 2;
  const height = imfActiveImageResolution.height / 2;
  const raw = { width, height } as Resolution;
  const rounded = ceilingResolutionValues(roundResolutionValues(raw), 2);

  return {
    raw,
    rounded,
  };
};

/**
 * Main calculation function for Framing and Working Resolution Calculator
 *
 * This is the primary entry point that orchestrates all resolution calculations for
 * a given camera sensor, output format, lens type, and safety requirements. It computes:
 *
 * - Sensor and content aspect ratios
 * - Active image resolution (framing decision area)
 * - IMF standard resolutions for digital cinema
 * - Maximum allowable sensor crop and resize limits
 * - Preferred and minimum working resolutions
 * - Netflix UHD compliance assessment
 *
 * All calculations account for anamorphic lens desqueeze factors and safety crops.
 * Results include both raw precise values and rounded values suitable for display.
 */
export const getFramingAndWorkingResolutionOutput = (
  props: FramingAndWorkingResolutionInputProps
): FramingAndWorkingResolutionOutputProps => {
  const canvasResolution = props.canvasResolution;
  const canvasAspectRatio = getAspectRatio(canvasResolution);
  const contentAspectRatio = getAspectRatio(canvasResolution, props.squeeze);
  const framingDecisionResolution = props.framingDecisionResolution;
  const imfActiveImageResolution = getIMFActiveImageResolution(
    props.aspectRatio
  );
  const maximumAllowedSensorCrop = getMaximumAllowedSensorCrop(props);
  const maximumAllowedResizeInPost = getMaximumAllowedResizeInPost(
    framingDecisionResolution,
    maximumAllowedSensorCrop.raw
  );
  const preferredWorkingResolution = getPreferredWorkingResolution(
    imfActiveImageResolution.raw,
    framingDecisionResolution,
    canvasResolution,
    contentAspectRatio.raw
  );
  const minimumWorkingResolution = getMinimumWorkingResolution(
    imfActiveImageResolution.raw,
    props.protection
  );
  const activeImagePixelCount = getMegaPixelCount(framingDecisionResolution);
  const dailiesActiveImageResolution = getDailiesActiveImageResolution(
    imfActiveImageResolution.raw
  );

  const lensSqueezeFactorIsOne = props.squeeze === 1;

  const activeImageWidthMeetsMinimum = framingDecisionResolution.width > 3839;
  const activeImageHeightMeetsMinimum = framingDecisionResolution.height > 2159;
  const activeImagePixelCountMeetsMinimum = activeImagePixelCount > 8.29;

  const activeImageAspectRatio = getAspectRatio(framingDecisionResolution);
  const activeImageAspectRatioMeetsMinimum =
    activeImageAspectRatio.raw[0] > 1.7777777777777;

  const approvedForNetflixUHD =
    (activeImageAspectRatioMeetsMinimum && activeImageWidthMeetsMinimum) ||
    (!activeImageAspectRatioMeetsMinimum &&
      activeImageHeightMeetsMinimum &&
      lensSqueezeFactorIsOne) ||
    activeImagePixelCountMeetsMinimum;

  return {
    sensorAspectRatio: canvasAspectRatio.rounded,
    contentAspectRatio: contentAspectRatio.rounded,
    maximumAllowedSensorCrop: maximumAllowedSensorCrop.rounded,
    maximumAllowedResizeInPost: maximumAllowedResizeInPost.rounded,
    imfActiveImageResolution: imfActiveImageResolution.rounded,
    dailiesActiveImageResolution: dailiesActiveImageResolution.rounded,
    preferredWorkingResolution: preferredWorkingResolution.rounded,
    minimumWorkingResolution: minimumWorkingResolution.rounded,
    approvedForNetflixUHD,
  };
};
